/**
 * Démonstration sans base (Démo — données fictives) : scénario « Gestion immobilière
 * Laurentides, 30 multilogements » du handoff Claude Design (`contrats-data.js`). Jamais
 * utilisé en repli d'une vraie base.
 */
import type {
  ServiceLocationDetail,
  VnextContractListItem,
  VnextContractKind,
  VnextLocationsOverview,
  VnextServiceLocation,
} from '../../domain/vnextContract'

const buildings: readonly [string, string, string, string, number][] = [
  ['245', 'rue Labelle', 'Saint-Jérôme', 'Le Labelle', 24],
  ['1180', 'boul. des Hauteurs', 'Saint-Jérôme', 'Les Hauteurs I', 36],
  ['1190', 'boul. des Hauteurs', 'Saint-Jérôme', 'Les Hauteurs II', 36],
  ['560', 'rue Saint-Georges', 'Saint-Jérôme', 'Carré Saint-Georges', 18],
  ['88', 'rue De Martigny O.', 'Saint-Jérôme', 'Résidence Martigny', 12],
  ['1415', 'boul. Grignon', 'Saint-Jérôme', 'Le Grignon', 30],
  ['312', 'rue Castonguay', 'Saint-Jérôme', 'Castonguay 312', 16],
  ['720', 'rue Brière', 'Saint-Jérôme', 'Les Jardins Brière', 42],
  ['45', 'rue Godmer', 'Saint-Jérôme', '', 8],
  ['1025', 'rue Latour', 'Saint-Jérôme', 'Place Latour', 20],
  ['390', 'rue Parent', 'Saint-Jérôme', 'Le Parent', 14],
  ['2150', 'boul. du Curé-Labelle', 'Saint-Jérôme', 'Curé-Labelle Nord', 48],
  ['67', 'rue Melançon', 'Saint-Jérôme', '', 6],
  ['815', 'rue Fournier', 'Saint-Jérôme', 'Le Fournier', 22],
  ['1300', 'rue Saint-Joseph', 'Saint-Jérôme', 'Domaine Saint-Joseph', 28],
  ['455', 'rue Bélanger', 'Saint-Jérôme', '', 10],
  ['940', 'boul. Lajeunesse', 'Saint-Jérôme', 'Lajeunesse 940', 18],
  ['3020', 'boul. de la Salette', 'Saint-Jérôme', 'Les Cimes Salette', 32],
  ['125', 'rue Rolland', 'Saint-Jérôme', '', 8],
  ['1760', 'rue Dumouchel', 'Saint-Jérôme', 'Le Dumouchel', 24],
  ['600', 'rue Ouimet', 'Saint-Jérôme', 'Habitations Ouimet', 16],
  ['2840', 'rue Laviolette', 'Saint-Jérôme', 'Laviolette', 20],
  ['3150', 'boul. du Curé-Labelle', 'Prévost', 'Le Prévostois', 26],
  ['1045', 'rue des Clos-Fleuris', 'Prévost', '', 12],
  ['480', 'rue Principale', 'Prévost', 'Place Principale', 18],
  ['17450', 'rue Victor', 'Mirabel', 'Le Victor', 30],
  ['9200', 'boul. de Saint-Canut', 'Mirabel', 'Saint-Canut 9200', 24],
  ['13550', 'rue Saint-Simon', 'Mirabel', '', 10],
  ['705', 'rue Lachaine', 'Saint-Jérôme', 'Lachaine 705', 14],
  ['1500', 'boul. Roland-Godard', 'Saint-Jérôme', 'Godard Est', 36],
]

const incomplete = new Set([7, 15, 24])
const routeA = new Set([1, 2, 3, 4, 5, 6, 8, 9, 10, 11])
const routeB = new Set([12, 13, 14, 16, 17, 18, 19, 20, 21])
const toConfirm = new Set([24, 28])
const specificContacts: Record<number, [string, string, string]> = {
  3: ['Luc', 'Paquette', 'Concierge'],
  11: ['Sonia', 'Bélisle', 'Surintendante'],
  18: ['Martin', 'Lauzon', 'Concierge'],
  27: ['Julie', 'Cyr', 'Gestionnaire'],
}

export const demoLaurentidesContractId = '00000000-0000-4000-a000-00000000d014'

export function demoLaurentidesLocations(): VnextServiceLocation[] {
  return buildings.map(([civic, street, city, site, units], index) => {
    const n = index + 1
    const status = n >= 26 ? 'NOT_STARTED' : incomplete.has(n) ? 'INCOMPLETE' : 'COMPLETE'
    const specific = specificContacts[n]
    const route = routeA.has(n)
      ? { id: 'route-a', name: 'Route A', color: '#2563EB' }
      : routeB.has(n)
        ? { id: 'route-b', name: 'Route B', color: '#D97706' }
        : undefined
    return {
      id: `00000000-0000-4000-a000-${String(n).padStart(12, '0')}`,
      order: n,
      formattedAddress: `${civic}, ${street}, ${city}`,
      line1: `${civic}, ${street}`,
      city,
      latitude: 45.78 + n * 0.002,
      longitude: -74.0 - n * 0.002,
      ...(site ? { siteName: site } : {}),
      unitCount: units,
      priceCents: Math.round((700 + units * 32) / 50) * 50 * 100,
      validationStatus: toConfirm.has(n) ? 'NEEDS_CONFIRMATION' : 'VALID',
      useContractDefaultContact: !specific,
      contact: specific
        ? { source: 'LOCATION', firstName: specific[0], lastName: specific[1], roleLabel: specific[2] }
        : { source: 'CONTRACT', firstName: 'Marie-Ève', lastName: 'Gagnon', roleLabel: 'Gestionnaire' },
      ...(route ? { route } : {}),
      readiness: {
        position: !toConfirm.has(n),
        contact: true,
        // Comme le handoff : 7 sans zone, 15 sans point d'entrée, 24 à confirmer.
        entry: status !== 'NOT_STARTED' && n !== 15,
        snowClearingZone: status === 'COMPLETE' || n === 15 || n === 24,
        status,
      },
    }
  })
}

export function demoLaurentidesOverview(): VnextLocationsOverview {
  const locations = demoLaurentidesLocations()
  const ready = locations.filter((location) => location.readiness.status === 'COMPLETE').length
  const assigned = locations.filter((location) => location.route).length
  return {
    contractId: demoLaurentidesContractId,
    contractType: 'MULTIFAMILY',
    pricingMode: 'PER_LOCATION',
    locations,
    totals: {
      total: locations.length,
      ready,
      toConfigure: locations.length - ready,
      assigned,
      unassigned: locations.length - assigned,
    },
  }
}

type DemoRow = [string, string, string, number, number, number, VnextContractListItem['status'], boolean]

const multifamilyRows: DemoRow[] = [
  ['MLT-00013', 'Habitations Rivière-du-Nord', 'Daniel Turcotte', 6, 6, 980000, 'ACTIVE', true],
  ['MLT-00012', 'Coop d’habitation Le Faubourg', 'Nathalie Roy', 1, 1, 215000, 'ACTIVE', true],
  ['MLT-00011', 'Groupe Immeubles Prévost', 'Éric Lalonde', 12, 9, 1840000, 'ACTIVE', true],
  ['MLT-00010', 'Gestion Lafontaine & fils', 'Chantal Dion', 4, 4, 625000, 'ACTIVE', true],
  ['MLT-00009', 'Résidences du Vieux-Saint-Jérôme', 'Pierre Bastien', 3, 0, 490000, 'DRAFT', false],
  ['MLT-00008', 'Immeubles Mirabel Sud', 'Karine Hébert', 8, 8, 1210000, 'ACTIVE', true],
]

const commercialRows: DemoRow[] = [
  ['COM-00021', 'Distribution Nordik', 'Alexandre Morin', 5, 3, 3850000, 'PENDING', false],
  ['COM-00020', 'Centre commercial Carrefour du Nord', 'Isabelle Fortin', 1, 1, 2400000, 'ACTIVE', true],
  ['COM-00019', 'Clinique dentaire Labelle', 'Dre Sophie Gauthier', 1, 0, 320000, 'DRAFT', false],
]

function rowsToItems(rows: DemoRow[], prefix: string): VnextContractListItem[] {
  return rows.map(([number, company, contact, total, ready, priceCents, status, signed], index) => ({
    id: `00000000-0000-4000-a${prefix}0-${String(index + 1).padStart(12, '0')}`,
    number,
    flow: 'VNEXT',
    company,
    contact,
    season: '2026-2027',
    priceCents,
    status,
    ...(signed ? { signedOn: '2026-10-01' } : {}),
    ...(status === 'PENDING' ? { sentAt: '2026-10-20T14:00:00Z' } : {}),
    locations: { total, ready, assigned: ready },
  }))
}

export function demoContractList(kind: VnextContractKind): VnextContractListItem[] {
  if (kind === 'COMMERCIAL') return rowsToItems(commercialRows, '0c')
  const overview = demoLaurentidesOverview()
  const laurentides: VnextContractListItem = {
    id: demoLaurentidesContractId,
    number: 'MLT-00014',
    flow: 'VNEXT',
    company: 'Gestion immobilière Laurentides',
    contact: 'Marie-Ève Gagnon',
    season: '2026-2027',
    priceCents: overview.locations.reduce((sum, location) => sum + (location.priceCents ?? 0), 0),
    status: 'PENDING',
    sentAt: '2026-10-21T15:30:00Z',
    locations: {
      total: overview.totals.total,
      ready: overview.totals.ready,
      assigned: overview.totals.assigned,
    },
  }
  return [laurentides, ...rowsToItems(multifamilyRows, '0m')]
}

/** Fiche d'une adresse de la démo (adresse 7 = « 312, rue Castonguay » de la maquette 5). */
export function demoLocationDetail(locationId: string): ServiceLocationDetail {
  const locations = demoLaurentidesLocations()
  const index = Math.max(
    0,
    locations.findIndex((location) => location.id === locationId),
  )
  const location = locations[index] ?? locations[0]
  if (!location) throw new Error('VNEXT_LOCATION_NOT_FOUND')
  const previous = locations[index - 1]
  const next = locations[index + 1]
  const complete = location.readiness.status === 'COMPLETE'
  const lat = location.latitude ?? 45.78
  const lng = location.longitude ?? -74
  return {
    id: location.id,
    contractId: demoLaurentidesContractId,
    contractNumber: 'MLT-00014',
    contractType: 'MULTIFAMILY',
    contractLocked: false,
    position: index + 1,
    total: locations.length,
    ...(previous ? { previousId: previous.id } : {}),
    ...(next ? { nextId: next.id } : {}),
    line1: location.line1,
    ...(location.city ? { city: location.city } : {}),
    formattedAddress: location.formattedAddress,
    latitude: lat,
    longitude: lng,
    ...(location.validationStatus ? { validationStatus: location.validationStatus } : {}),
    ...(location.siteName ? { siteName: location.siteName } : {}),
    ...(location.unitCount !== undefined ? { unitCount: location.unitCount } : {}),
    ...(location.priceCents !== undefined ? { priceCents: location.priceCents } : {}),
    profile: complete
      ? {
          tenantAccess: 'Porte avant et escalier arrière',
          importantHours: 'Départs 6 h 30',
          municipalEdging: true,
        }
      : {},
    useContractDefaultContact: location.contact?.source !== 'LOCATION',
    contacts:
      location.contact?.source === 'LOCATION'
        ? [
            {
              role: 'PRIMARY',
              ...(location.contact.firstName ? { firstName: location.contact.firstName } : {}),
              ...(location.contact.lastName ? { lastName: location.contact.lastName } : {}),
              phone: '450 555-0187',
              ...(location.contact.roleLabel ? { roleLabel: location.contact.roleLabel } : {}),
            },
          ]
        : [],
    entries: location.readiness.entry
      ? [
          {
            label: 'Entrée principale',
            entryType: 'MAIN',
            latitude: lat + 0.0001,
            longitude: lng,
            detectionRadiusM: 25,
            enabled: true,
            isPrimary: true,
          },
        ]
      : [],
    zones: complete
      ? [
          {
            label: 'Stationnement principal',
            kind: 'SNOW_CLEARING',
            surfaceM2: 1240,
            geometry: {
              type: 'Polygon',
              coordinates: [
                [
                  [lng - 0.0002, lat - 0.0001],
                  [lng + 0.0002, lat - 0.0001],
                  [lng + 0.0002, lat + 0.0001],
                  [lng - 0.0002, lat + 0.0001],
                  [lng - 0.0002, lat - 0.0001],
                ],
              ],
            },
          },
        ]
      : [],
    ...(location.route ? { route: location.route } : {}),
    readiness: location.readiness,
  }
}
